import { ConfigurationPermissionStatus } from "@/components/configuration";
import { DEFAULT_PERMISSION_STATUS_STATE } from "@/components/desktop/system-permissions/shared/system-permissions.utils";
import { sleep } from "@/domains/event-loop/synchronization";
import { RecallPermissionKind, RecallPermissionStatus } from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { useCallback, useMemo, useState } from "react";

export const useSystemPermissionHandlers = ({
  onRequestPermission,
  onOpenPermissionPane,
  permissionStatusMap,
}: {
  onRequestPermission: (permission: RecallPermissionKind) => Promise<void>;
  onOpenPermissionPane: (permission: RecallPermissionKind) => Promise<void>;
  permissionStatusMap: Partial<Record<RecallPermissionKind, RecallPermissionStatus>>;
}): {
  resolveConfigurationStatus: ({
    kind,
  }: {
    kind: RecallPermissionKind;
  }) => ConfigurationPermissionStatus;
  handleEnablePermission: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  handleOpenPermissionPane: ({ kind }: { kind: RecallPermissionKind }) => Promise<void>;
  restartRequired: boolean;
} => {
  const [loadingPermission, setLoadingPermission] = useState<RecallPermissionKind | null>(null);
  const [hasAttemptedToEnablePermission, setHasAttemptedToEnablePermission] = useState<
    Record<RecallPermissionKind, boolean>
  >(DEFAULT_PERMISSION_STATUS_STATE);

  const handleEnablePermission = useCallback(
    async ({ kind }: { kind: RecallPermissionKind }) => {
      if (loadingPermission) return;

      logger.info({
        message: "[MeetingPermissionsModal] Permission button clicked",
        info: { kind },
      });

      setLoadingPermission(kind);
      await sleep(1500);

      try {
        await onRequestPermission(kind);
      } finally {
        setHasAttemptedToEnablePermission(prev => ({
          ...prev,
          [kind]: true,
        }));

        setLoadingPermission(null);
      }
    },
    [loadingPermission, onRequestPermission]
  );

  const handleOpenPermissionPane = useCallback(
    async ({ kind }: { kind: RecallPermissionKind }) => {
      if (loadingPermission) return;

      logger.info({
        message: "[MeetingPermissionsModal] Opening system preferences",
        info: { kind },
      });

      setLoadingPermission(kind);

      await sleep(1500);

      try {
        await onOpenPermissionPane(kind);
      } finally {
        setHasAttemptedToEnablePermission(prev => ({
          ...prev,
          [kind]: true,
        }));

        setLoadingPermission(null);
      }
    },
    [loadingPermission, onOpenPermissionPane]
  );

  const resolveConfigurationStatus = useCallback(
    ({ kind }: { kind: RecallPermissionKind }): ConfigurationPermissionStatus => {
      if (loadingPermission === kind) {
        return ConfigurationPermissionStatus.Loading;
      }

      const status = permissionStatusMap[kind];

      if (status === RecallPermissionStatus.Granted) {
        return ConfigurationPermissionStatus.Granted;
      }

      // This is disabled to avoid the extra dialog about recording screen without selection.
      // /**
      //  * If someone requests `SystemAudio`...
      //  * ...and they don't have it, but they DO have `ScreenCapture`,...
      //  * ...then it is sufficient, and we can treat it as "granted"
      //  */
      // if (kind === RecallPermissionKind.SystemAudio) {
      //   /**
      //    * We check for `ScreenCapture`
      //    * If they have it, we will still return `Granted` for kind == `SystemAudio`
      //    */
      //   const screenCaptureStatus = permissionStatusMap[RecallPermissionKind.ScreenCapture];
      //   if (screenCaptureStatus === RecallPermissionStatus.Granted) {
      //     return ConfigurationPermissionStatus.Granted;
      //   }
      // }

      if (status === RecallPermissionStatus.Denied) {
        return ConfigurationPermissionStatus.Denied;
      }

      // If it hasn't been granted, but we HAVE attempted to enable it,
      // we treat it as "timeout"
      if (hasAttemptedToEnablePermission[kind]) {
        return ConfigurationPermissionStatus.Timeout;
      }

      if (status === RecallPermissionStatus.NotRequested) {
        return ConfigurationPermissionStatus.Initial;
      }

      return ConfigurationPermissionStatus.Initial;
    },
    [hasAttemptedToEnablePermission, loadingPermission, permissionStatusMap]
  );

  const restartRequired = useMemo(() => {
    if (hasAttemptedToEnablePermission[RecallPermissionKind.ScreenCapture]) {
      return true;
    }

    if (hasAttemptedToEnablePermission[RecallPermissionKind.FullDiskAccess]) {
      return true;
    }

    return false;
  }, [hasAttemptedToEnablePermission]);

  return {
    resolveConfigurationStatus,
    handleEnablePermission,
    handleOpenPermissionPane,
    restartRequired,
  };
};
